import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { CONTENT_KEYS } from '@core/models/content-keys';
import { ContentService } from '@core/services/content.service';
import { LanguageService } from '@core/services/language.service';

import { GroupsRenderer } from '@shared/components/groups-renderer/groups-renderer';
import type { ComponentGroup } from '@shared/models/dynamic-layout.types';
import type { DynamicAction } from '@shared/registry';

import { AppointmentsService } from '@swagger-services/forthbridge-appointment/api/appointments.service';

import { environment } from 'src/environments/environment';

import type { ScheduledVisitCardConfig, ScheduledVisitCardContent } from './scheduled-visit-card.types';

@Component({
  selector: 'app-scheduled-visit',
  templateUrl: './scheduled-visit-card.html',
  imports: [GroupsRenderer],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ContentService],
})
export class ScheduledVisitCard {
  private readonly contentService = inject(ContentService<ScheduledVisitCardContent>);
  private readonly appointmentsService = inject(AppointmentsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly languageService = inject(LanguageService);

  readonly config = input.required<ScheduledVisitCardConfig>();
  readonly disabled = input<boolean>(false);

  readonly action = output<DynamicAction>();

  private readonly enteredEarlyWindow = signal(false);
  private readonly queuePosition = signal<number | null>(null);

  private readonly checkIn = computed<string | null>(() => {
    const sessions = (this.config().appointment.workflowSessions ?? []) as Array<{
      type?: string;
      status?: string;
      workflowKey?: string;
    }>;
    return sessions.find((s) => s.type === 'check-in' && s.status !== 'done')?.workflowKey ?? null;
  });

  private readonly isSoon = computed(() => {
    const appointment = this.config().appointment;
    if (appointment.schedulingMode === 'immediate') return true;
    if (this.enteredEarlyWindow()) return true;

    const bufferMinutes =
      appointment.waitingRoom?.earlyAccessBufferInMinutes ?? environment.defaultEarlyVisitJoinWindowMinutes;
    const now = new Date();
    const startTime = new Date(appointment.startDateTime);
    const minutesUntilStart = (startTime.getTime() - now.getTime()) / (1000 * 60);
    return minutesUntilStart <= bufferMinutes;
  });

  private readonly returnUrl = computed(() => this.config().returnUrl ?? this.router.url);

  private readonly waitingRoomUrl = computed(() => {
    const base = `/appointment/${this.config().appointment.id}/waiting-room`;
    return `${base}?returnUrl=${encodeURIComponent(this.returnUrl())}`;
  });

  private readonly visitDetailsUrl = computed(() => {
    const base = `/appointment/${this.config().appointment.id}/visit-details`;
    return `${base}?returnUrl=${encodeURIComponent(this.returnUrl())}`;
  });

  private readonly rawContent = this.contentService.rawContent;

  private readonly jexlContext = computed<Record<string, unknown>>(() => {
    const { appointment, variant, chipVariant, canDismiss, dismissAction, shadow } = this.config();
    const isVirtual = !appointment?.location || appointment.location.locationTypeCategory === 'virtual';
    const isImmediate = appointment.schedulingMode === 'immediate';
    const isSoon = this.isSoon();
    const checkIn = this.checkIn();
    const needsCheckIn = checkIn !== null;

    return {
      appointment,
      isVirtual,
      isInPerson: !isVirtual,
      isImmediate,
      checkIn,
      needsCheckIn,
      isSoon,
      queuePosition: this.queuePosition(),
      returnUrl: this.returnUrl(),
      waitingRoomUrl: this.waitingRoomUrl(),
      visitDetailsUrl: this.visitDetailsUrl(),
      variant: variant ?? 'highlighted',
      chipVariant: chipVariant ?? 'subtle',
      canDismiss: canDismiss ?? false,
      dismissAction: dismissAction ?? null,
      shadow: shadow ?? false,
    };
  });

  private static readonly defaultLoadingGroups: ComponentGroup[] = [
    {
      id: 'skeleton-wrapper',
      layout: {
        direction: 'column',
        gap: 'md',
        className: 'px-(--visitcard-padding-x) py-(--visitcard-padding-y) rounded-(--visitcard-border-radius)',
      },
      items: [
        {
          id: 'sk-text',
          itemType: 'component',
          componentType: 'Skeleton',
          props: { config: { height: '80px', width: '60%' } },
        },
        {
          id: 'sk-subtext',
          itemType: 'component',
          componentType: 'Skeleton',
          props: { config: { height: '16px', width: '75%' } },
        },
        {
          id: 'sk-btn1',
          itemType: 'component',
          componentType: 'Skeleton',
          props: { config: { height: '44px', borderRadius: 'full' } },
        },
        {
          id: 'sk-btn2',
          itemType: 'component',
          componentType: 'Skeleton',
          props: { config: { height: '44px', borderRadius: 'full' } },
        },
      ],
    } as ComponentGroup,
  ];

  protected readonly isLoading = computed(() => {
    const state = this.contentService.state();
    return state.isInitial() || state.isLoading();
  });

  protected readonly loadingGroups = computed(() => {
    const groups = this.config().loadingGroups ?? ScheduledVisitCard.defaultLoadingGroups;
    return this.contentService.interpolateGroups(groups, this.jexlContext());
  });

  protected readonly groups = computed<ComponentGroup[]>(() => {
    const content = this.rawContent();
    return this.contentService.interpolateGroups(content?.groups ?? [], this.jexlContext());
  });

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();
      untracked(() => {
        this.contentService
          .loadFeatureContent(CONTENT_KEYS.COMPONENTS._id, CONTENT_KEYS.COMPONENTS.SCHEDULED_VISIT_CARD)
          .catch(console.error);
      });
    });

    afterNextRender(() => {
      this.startQueuePositionPolling();
    });

    effect((onCleanup) => {
      const appointment = this.config().appointment;
      if (appointment.schedulingMode === 'immediate') return;

      const bufferMinutes =
        appointment.waitingRoom?.earlyAccessBufferInMinutes ?? environment.defaultEarlyVisitJoinWindowMinutes;
      const startTime = new Date(appointment.startDateTime);
      const earlyJoinTime = new Date(startTime.getTime() - bufferMinutes * 60 * 1000);
      const msUntilEarlyJoin = earlyJoinTime.getTime() - Date.now();

      if (msUntilEarlyJoin <= 0) {
        this.enteredEarlyWindow.set(true);
        return;
      }

      // setTimeout uses a 32-bit signed int for delay; larger values overflow and fire immediately.
      const maxTimeoutDelay = 2_147_483_647;
      if (msUntilEarlyJoin > maxTimeoutDelay) return;

      const timeoutId = setTimeout(() => this.enteredEarlyWindow.set(true), msUntilEarlyJoin);
      onCleanup(() => clearTimeout(timeoutId));
    });
  }

  protected onAction(event: DynamicAction): void {
    this.action.emit(event);
  }

  private startQueuePositionPolling(): void {
    const { appointment, shouldPollQueue = true } = this.config();
    if (!shouldPollQueue || appointment.schedulingMode !== 'immediate') return;

    let intervalId: ReturnType<typeof setInterval> | null = null;
    const baseUrl = this.router.url.split('?')[0];

    const startPolling = () => {
      if (intervalId) return;
      this.fetchQueuePosition();
      intervalId = setInterval(() => this.fetchQueuePosition(), 60000);
    };

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    startPolling();

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (!(event instanceof NavigationEnd)) return;
      const currentUrl = event.urlAfterRedirects.split('?')[0];
      if (currentUrl === baseUrl) {
        startPolling();
      } else {
        stopPolling();
      }
    });

    this.destroyRef.onDestroy(() => stopPolling());
  }

  private fetchQueuePosition(): void {
    this.appointmentsService
      .appointmentsControllerGetQueuePosition('', this.config().appointment.id)
      .then((response) => this.queuePosition.set(response.position))
      .catch(() => this.queuePosition.set(null));
  }
}
